import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NewsletterApiService } from 'shared/services/newsletter-api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { DateTime } from 'luxon';
import { Subscription, take, debounceTime, distinctUntilChanged } from 'rxjs';
import { Campaign } from '../campaigns.component';
import { FilterRecipientsDialogComponent } from '../filter-recipients-dialog/filter-recipients-dialog.component';

@Component({
  selector: 'app-campaign-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatLuxonDateModule,
    MatTooltipModule,
    MatSlideToggleModule
  ],
  templateUrl: './campaign-editor.component.html',
  styleUrls: ['./campaign-editor.component.scss']
})
export class CampaignEditorComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];

  form: FormGroup;
  isLoading = false;
  isSaving = false;
  isEdit = false;
  campaignId: string | null = null;
  campaign: Campaign | null = null;

  // Preview
  previewHtml: SafeHtml = '';
  isLoadingPreview = false;

  // Test recipient
  testEmail = '';
  isAddingTestRecipient = false;

  // Test email send
  testSendEmail = '';
  isSendingTestEmail = false;

  constructor(
    private _fb: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _newsletterApi: NewsletterApiService,
    private _snackbar: SnackbarService,
    private _dialog: MatDialog,
    private _sanitizer: DomSanitizer
  ) {
    this.form = this._fb.group({
      name: ['', Validators.required],
      subject: ['', Validators.required],
      htmlContent: [''],
      sendImmediately: [true],
      startDate: [DateTime.now()],
      startTime: ['09:00']
    });

    // Live preview z debounce (żeby nie wysyłać zbyt wielu requestów)
    this.form.get('htmlContent')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(html => {
      this._updatePreview(html);
    });

    // Załaduj pusty szablon na start
    this._updatePreview('');
  }

  ngOnInit(): void {
    // Nasłuchuj na zmiany parametrów route (ważne przy przejściu z /new do /:id)
    const sub = this._route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      // Jeśli to ten sam ID co już mamy - nie ładuj ponownie
      if (this.campaignId === id && this.campaign) {
        return;
      }
      
      this.campaignId = id;
      this.isEdit = id !== 'new' && id !== null;

      if (this.isEdit && this.campaignId) {
        this._loadCampaign(this.campaignId);
      } else {
        // Reset dla nowej kampanii
        this.campaign = null;
        this.form.reset({
          name: '',
          subject: '',
          htmlContent: '',
          sendImmediately: true,
          startDate: DateTime.now(),
          startTime: '09:00'
        });
        this.previewHtml = '';
      }
    });
    this._subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  private _loadCampaign(id: string): void {
    this.isLoading = true;
    this._newsletterApi.getCampaign(id).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.success) {
          this.campaign = response.data;
          this.form.patchValue({
            name: this.campaign!.name,
            subject: this.campaign!.subject,
            htmlContent: this.campaign!.htmlContent,
            sendImmediately: (this.campaign as any).sendImmediately !== false,
            startDate: this.campaign!.startDate ? DateTime.fromISO(this.campaign!.startDate) : DateTime.now(),
            startTime: this.campaign!.startTime || '09:00'
          });
          this._updatePreview(this.campaign!.htmlContent);
        } else {
          this._snackbar.showSnackBar('Nie udało się załadować kampanii', 'error');
          this._router.navigate(['/admin/campaigns']);
        }
      },
      error: () => {
        this.isLoading = false;
        this._snackbar.showSnackBar('Błąd ładowania kampanii', 'error');
        this._router.navigate(['/admin/campaigns']);
      }
    });
  }

  private _updatePreview(html: string): void {
    this.isLoadingPreview = true;
    
    this._newsletterApi.getPreview(html || '').pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isLoadingPreview = false;
        if (response.success && response.data?.html) {
          this.previewHtml = this._sanitizer.bypassSecurityTrustHtml(response.data.html);
        }
      },
      error: () => {
        this.isLoadingPreview = false;
        // Fallback do lokalnego preview
        this.previewHtml = this._sanitizer.bypassSecurityTrustHtml(html || '');
      }
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const formValue = this.form.value;

    const payload: any = {
      name: formValue.name,
      subject: formValue.subject,
      htmlContent: formValue.htmlContent,
      sendImmediately: formValue.sendImmediately
    };

    // Dodaj datę/godzinę tylko jeśli nie wysyłamy natychmiast
    if (!formValue.sendImmediately) {
      payload.startDate = formValue.startDate instanceof DateTime 
        ? formValue.startDate.toISO() 
        : formValue.startDate;
      payload.startTime = formValue.startTime;
    }

    const request$ = this.isEdit && this.campaignId
      ? this._newsletterApi.updateCampaign(this.campaignId, payload)
      : this._newsletterApi.createCampaign(payload);

    request$.pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        if (response.success) {
          this._snackbar.showSnackBar(
            this.isEdit ? 'Kampania zaktualizowana' : 'Kampania utworzona',
            'success'
          );
          
          // Aktualizuj lokalny stan
          this.campaign = response.data;
          
          if (!this.isEdit) {
            // Po utworzeniu - zaktualizuj URL i stan
            this.campaignId = response.data._id;
            this.isEdit = true;
            // Zmień URL bez przeładowania
            this._router.navigate(['/admin/campaigns', response.data._id], { replaceUrl: true });
          }
        } else {
          this._snackbar.showSnackBar(response.message || 'Błąd', 'error');
        }
      },
      error: () => {
        this.isSaving = false;
        this._snackbar.showSnackBar('Wystąpił błąd', 'error');
      }
    });
  }

  openFilterDialog(): void {
    if (!this.campaignId || this.campaignId === 'new') {
      this._snackbar.showSnackBar('Najpierw zapisz kampanię', 'warning');
      return;
    }

    if (!this.campaign) {
      this._snackbar.showSnackBar('Ładowanie kampanii...', 'info');
      return;
    }

    const dialogRef = this._dialog.open(FilterRecipientsDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { campaign: this.campaign }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.campaignId) {
        this._loadCampaign(this.campaignId);
      }
    });
  }

  goBack(): void {
    this._router.navigate(['/admin/campaigns']);
  }

  addTestRecipient(): void {
    if (!this.testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.testEmail)) {
      this._snackbar.showSnackBar('Podaj prawidłowy adres e-mail', 'warning');
      return;
    }

    if (!this.campaignId || this.campaignId === 'new') {
      this._snackbar.showSnackBar('Najpierw zapisz kampanię', 'warning');
      return;
    }

    this.isAddingTestRecipient = true;
    this._newsletterApi.addTestRecipient(this.campaignId, this.testEmail).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isAddingTestRecipient = false;
        if (response.success) {
          this._snackbar.showSnackBar(response.message || 'Dodano odbiorcę testowego', 'success');
          this.testEmail = '';
          // Odśwież kampanię
          if (this.campaignId) {
            this._loadCampaign(this.campaignId);
          }
        } else {
          const errorMessages: Record<string, string> = {
            'recipient-already-exists': 'Ten email jest już na liście odbiorców',
            'cannot-modify-active-campaign': 'Nie można modyfikować aktywnej kampanii'
          };
          this._snackbar.showSnackBar(errorMessages[response.errorCode] || response.message || 'Błąd', 'error');
        }
      },
      error: () => {
        this.isAddingTestRecipient = false;
        this._snackbar.showSnackBar('Wystąpił błąd', 'error');
      }
    });
  }

  sendTestEmail(): void {
    if (!this.testSendEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.testSendEmail)) {
      this._snackbar.showSnackBar('Podaj prawidłowy adres e-mail', 'warning');
      return;
    }

    if (!this.campaignId || this.campaignId === 'new') {
      this._snackbar.showSnackBar('Najpierw zapisz kampanię', 'warning');
      return;
    }

    // Sprawdź czy kampania ma treść
    const htmlContent = this.form.get('htmlContent')?.value;
    const subject = this.form.get('subject')?.value;
    if (!htmlContent || !subject) {
      this._snackbar.showSnackBar('Uzupełnij temat i treść kampanii', 'warning');
      return;
    }

    this.isSendingTestEmail = true;
    this._newsletterApi.sendTestEmail(this.campaignId, this.testSendEmail).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isSendingTestEmail = false;
        if (response.success) {
          this._snackbar.showSnackBar(response.message || 'Wysłano testową wiadomość', 'success');
        } else {
          const errorMessages: Record<string, string> = {
            'campaign-incomplete': 'Uzupełnij temat i treść kampanii',
            'error-sending-test-email': 'Nie udało się wysłać wiadomości testowej'
          };
          this._snackbar.showSnackBar(errorMessages[response.errorCode] || response.message || 'Błąd', 'error');
        }
      },
      error: () => {
        this.isSendingTestEmail = false;
        this._snackbar.showSnackBar('Wystąpił błąd podczas wysyłania', 'error');
      }
    });
  }
}
